/* global React, Icon, PhoneMockup */

// Phase C: in-page Case section now sources its numbers from
// window.HEALBOT_CASE_STUDY.metrics (same source-of-truth as Hero.jsx). No
// inline numeric literals remain — when data is null, fall back to the
// dictionary `case.pdf.empty` ("—" by default) so the section never
// invents fake metrics.
//
// CTA: a "Read full case study →" link is added at the bottom of the
// section pointing at `/case/{slug}/`. The legacy `id="case"` anchor on the
// outer <section> is preserved so `#case` deep links keep scrolling here
// (Risk R7 in the plan).
//
// Architecture: file ends with `window.Case = Case;`; uses only Icon and
// PhoneMockup primitives; no cross-section imports. Per
// `.ai-factory/ARCHITECTURE.md` §"Ключевые принципы".

function Case() {
  const t = (k) => window.i18n.t(k);

  const locale = (window.i18n && window.i18n.getLocale && window.i18n.getLocale()) || "uk";
  const numberLocale =
    (window.i18n && window.i18n.numberLocale && window.i18n.numberLocale(locale)) || "uk-UA";
  const empty = t("case.pdf.empty");

  // Locale-correct number format with empty-state fallback. Returns "—"
  // (or whichever character the locale's `case.pdf.empty` resolves to)
  // when value is null.
  const fmt = (value) => {
    if (value == null || typeof value !== "number" || Number.isNaN(value)) return empty;
    try {
      return new Intl.NumberFormat(numberLocale).format(value);
    } catch (_e) {
      return String(value);
    }
  };

  // Pull metric values from the case-study source of truth. The 3 metrics
  // here mirror the Hero's 3 tiles — drift is impossible because both read
  // the same window.HEALBOT_HERO_METRICS via the metrics array's `key`
  // pointer, EXCEPT that the in-page Case section uses domain-specific
  // labels ("Записів через бот" etc.) instead of the shared tile labels.
  const cs = (typeof window !== "undefined" && window.HEALBOT_CASE_STUDY) || {};
  const heroMetrics = (typeof window !== "undefined" && window.HEALBOT_HERO_METRICS) || {};
  const csMetrics = Array.isArray(cs.metrics) ? cs.metrics : [];

  // Resolve each in-page line to a value. Uses the metric's `key` pointer
  // into HEALBOT_HERO_METRICS so a single edit in heroMetrics.js updates
  // both Hero and the Case PDF preview at once.
  const resolveValue = (key) => {
    const fromHero = heroMetrics[key];
    if (fromHero && fromHero.value != null) return fromHero.value;
    const fromCs = csMetrics.find((m) => m && m.key === key);
    return fromCs && fromCs.value != null ? fromCs.value : null;
  };
  const v1 = resolveValue("bookingsUplift"); // % uplift
  const v2 = resolveValue("frontDeskHours"); // hrs/week
  const v3 = resolveValue("avgReplyTime"); // minutes
  // Satisfaction is intentionally not in HEALBOT_HERO_METRICS — kept
  // null/empty here until product publishes a 4th metric.
  const v4 = null;

  const caseStudyEnabled =
    typeof window !== "undefined" && window.__HB_CASE_STUDY_ENABLED__ === true;
  const slug = cs.slug || null;
  const localePrefix = locale === "uk" ? "" : "/" + locale;
  const caseHref = caseStudyEnabled && slug ? localePrefix + "/case/" + slug + "/" : null;

  const trackCaseClick = () => {
    try {
      if (window.analytics && typeof window.analytics.track === "function") {
        window.analytics.track("case_study_cta_click", { source: "case_section", slug });
      }
      if (window.__HB_DEBUG__) {
        // eslint-disable-next-line no-console
        console.debug("[case-study] case_study_cta_click", { source: "case_section", slug });
      }
    } catch (_e) {
      // never break navigation on analytics failure
    }
  };

  return (
    <section className="section" id="case">
      <div className="case">
        <div className="case-inner">
          <div className="case-left">
            <div className="case-brand">{t("case.brand")}</div>
            <p className="case-quote">{t("case.quote")}</p>
            {caseHref && (
              <a href={caseHref} className="link-arrow case-cta-full" onClick={trackCaseClick}>
                {t("case.cta.full")}
              </a>
            )}
          </div>
          <div className="case-visual">
            <div className="case-pdf" aria-label={t("case.pdf.aria")}>
              <div className="pdf-ttl">{t("case.pdf.title")}</div>
              <div className="pdf-sub">{t("case.pdf.sub")}</div>
              {/*
                Bar heights are visual-only; they do not encode any specific
                metric value. They are kept stable across locales for layout
                consistency. The numeric `<strong>` cells below ARE
                source-of-truth-driven and fall back to "—" when null.
              */}
              <div className="bar-row">
                <div className="bar low" style={{ height: "30%" }}></div>
                <div className="bar low" style={{ height: "42%" }}></div>
                <div className="bar" style={{ height: "55%" }}></div>
                <div className="bar" style={{ height: "68%" }}></div>
                <div className="bar" style={{ height: "62%" }}></div>
                <div className="bar" style={{ height: "82%" }}></div>
                <div className="bar" style={{ height: "94%" }}></div>
              </div>
              <div className="pdf-line">
                <span>{t("case.pdf.line.1")}</span>
                <strong>{fmt(v1)}</strong>
              </div>
              <div className="pdf-line">
                <span>{t("case.pdf.line.2")}</span>
                <strong>{fmt(v2)}</strong>
              </div>
              <div className="pdf-line">
                <span>{t("case.pdf.line.3")}</span>
                <strong>{fmt(v3)}</strong>
              </div>
              <div className="pdf-line">
                <span>{t("case.pdf.line.4")}</span>
                <strong>{fmt(v4)}</strong>
              </div>
            </div>
            <PhoneMockup variant="case" />
          </div>
        </div>
      </div>
    </section>
  );
}

window.Case = Case;
